/**
 * Field Mappings — Sync & Integrations
 *
 * Named mapping sets per connector. Each set is a collection of
 * field-to-field translation rules. Multiple sets per connector
 * are supported (e.g. "Default", "Sprint Planning Sync").
 *
 * Per-connector you choose which set fires on import and which fires
 * on export — they can be the same set or different ones.
 *
 * All eight connectors are always available for configuration,
 * regardless of whether they are currently connected.
 */

import { ENGINE_WRITABLE_FIELDS } from '../../../../supabase/functions/_shared/fieldSyncOutbound';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowRight, ArrowLeftRight, ArrowDown, ArrowUp,
  Plus, Trash2, Save, Check, ChevronDown, ChevronRight,
  AlertCircle, ToggleLeft, ToggleRight, Info,
  Pencil, Copy, Wifi, WifiOff, } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  getAllConnectors,
  CONNECTOR_FIELDS,
  INTERNAL_FIELDS,
  INTERNAL_FIELD_CATEGORIES,
  type FieldMappingEntry,
  type FieldMappingSet,
  type FieldMappingConfig,
  type MappingDirection,
  type ExternalFieldDef,
  type InternalFieldDef,
  loadFieldMappingConfig,
  saveFieldMappingConfig,
  parseFieldMappingConfig,
  getDefaultJiraMappingSet,
  type RelationshipCueRule,
  type RelationshipCueKind,
} from '../../../lib/fieldMappingRegistry';
import { ATTRIBUTE_TARGET_PREFIX, CUSTOM_FIELD_TARGET_PREFIX, attributeKeyOfTarget, customFieldKeyOfTarget } from '../../../lib/fieldMappingApply';
import { humanizeAttributeKey } from '../../../lib/sessionCriteriaFromTrack';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import type { Attribute } from '../../../types/prioritization/lifecycle';
import {
  getConnectorProfile,
  getDefaultRelationshipMap,
} from '../../../lib/semanticConnectors/profiles';
import { fetchConnectorConfigsFromDB } from '../../../lib/connectors';
import { WhiteboardVisualLanguageSection } from '../../../components/prioritization/connectors/WhiteboardVisualLanguageSection';
import { ApplyMappingSetPanel } from '../../../components/prioritization/connectors/ApplyMappingSetPanel';
import { btnSecondary } from '../../../components/mono/primitives';

import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useSemanticConnectorsEnabled } from '../../../features/semantic-connectors/hooks/useSemanticConnectorsEnabled';
import { UnifiedAttributesSection } from './UnifiedAttributesSection';

// ── Helpers ───────────────────────────────────────────────────────────────────

function uid() { return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; }

function now() { return new Date().toISOString(); }

const ATTRIBUTES_SETTINGS_PATH = '/prioritization/settings/attributes';

/** The workspace's own scoring attributes as rule targets (`attribute:<key>`). */
function attributeTargets(attributes: Attribute[]): InternalFieldDef[] {
  return attributes
    .filter((a) => !a.isCustomField)
    .map((a): InternalFieldDef => ({
      key: `${ATTRIBUTE_TARGET_PREFIX}${a.key}`,
      label: a.label || humanizeAttributeKey(a.key),
      // Value lists take a number per external value, as the built-in list did;
      // text / date / yes-no attributes take the external value as it is.
      type: a.type === 'text' ? 'text' : a.type === 'date' ? 'date' : a.type === 'boolean' ? 'boolean' : 'number',
      category: 'attribute',
      description: a.definition,
    }));
}

/**
 * The workspace's own custom fields as rule targets (`custom_field:<key>`,
 * Settings → Custom Fields). A select field lists its options as the values a
 * value list may name; the rest take the external value as it is.
 */
function customFieldTargets(attributes: Attribute[]): InternalFieldDef[] {
  return attributes
    .filter((a) => a.isCustomField === true && a.enabled !== false && (a.objectType ?? 'item') === 'item')
    .map((a): InternalFieldDef => ({
      key: `${CUSTOM_FIELD_TARGET_PREFIX}${a.key}`,
      label: a.label || humanizeAttributeKey(a.key),
      type:
        a.type === 'select' ? 'enum'
        : a.type === 'multiselect' ? 'multi_enum'
        : a.type === 'number' ? 'number'
        : a.type === 'date' ? 'date'
        : a.type === 'boolean' ? 'boolean'
        : 'text',
      category: 'custom_field',
      description: a.definition,
      ...(a.options?.length ? { enumValues: a.options.map((o) => ({ value: o, label: o })) } : {}),
    }));
}

/** Target groups; the attribute and custom-field groups list this workspace's own, not fixed examples. */
function groupInternalFields(attributeFields: InternalFieldDef[], customFields: InternalFieldDef[] = []): Record<string, InternalFieldDef[]> {
  const groups: Record<string, InternalFieldDef[]> = {};
  for (const f of INTERNAL_FIELDS) {
    if (f.category === 'attribute') {
      groups.attribute ??= [...attributeFields];
      continue;
    }
    if (!groups[f.category]) groups[f.category] = [];
    groups[f.category].push(f);
  }
  if (customFields.length > 0) groups.custom_field = [...customFields];
  return groups;
}

/** A rule target's definition; a saved attribute or custom-field target the workspace lacks still resolves. */
function internalFieldDef(key: string, attributeFields: InternalFieldDef[], customFields: InternalFieldDef[] = []): InternalFieldDef | undefined {
  const customFieldKey = customFieldKeyOfTarget(key);
  if (customFieldKey) {
    return customFields.find((f) => f.key === key)
      ?? { key, label: humanizeAttributeKey(customFieldKey), type: 'text', category: 'custom_field' };
  }
  const attributeKey = attributeKeyOfTarget(key);
  if (!attributeKey) return INTERNAL_FIELDS.find((f) => f.key === key);
  return attributeFields.find((f) => f.key === key)
    ?? INTERNAL_FIELDS.find((f) => f.key === key)
    ?? { key, label: humanizeAttributeKey(attributeKey), type: 'number', category: 'attribute' };
}

function needsValueMap(extField?: ExternalFieldDef, intField?: InternalFieldDef): boolean {
  if (!extField || !intField) return false;
  return (
    (extField.type === 'enum' || extField.type === 'multi_enum') &&
    (intField.type === 'enum' || intField.type === 'multi_enum' || intField.type === 'number')
  );
}

// ── Value Map Editor ──────────────────────────────────────────────────────────

function ValueMapEditor({
  extField, intField, valueMap, onChange, internalChoices,
}: {
  extField: ExternalFieldDef;
  intField: InternalFieldDef;
  valueMap: Array<{ externalValue: string; internalValue: string }>;
  onChange: (vm: Array<{ externalValue: string; internalValue: string }>) => void;
  /** The values the target takes, when it names them (an attribute scale's points). */
  internalChoices?: Array<{ value: string; label: string }>;
}) {
  const externalValues = extField.enumValues ?? [];
  const internalOptions = internalChoices ?? intField.enumValues?.map(v => ({ value: v.value, label: v.label })) ?? [];
  const isNumeric = intField.type === 'number' && internalOptions.length === 0;

  const getVal = (ev: string) => valueMap.find(v => v.externalValue === ev)?.internalValue ?? '';
  const setVal  = (externalValue: string, internalValue: string) => {
    const next = valueMap.filter(v => v.externalValue !== externalValue);
    if (internalValue) next.push({ externalValue, internalValue });
    onChange(next);
  };

  if (!externalValues.length) {
    // A field whose values the other system names freely (Linear or Jira labels):
    // each value is typed as it appears there, e.g. "Impact: 9".
    return (
      <FreeValueMapEditor
        valueMap={valueMap}
        onChange={onChange}
        internalOptions={internalOptions}
        isNumeric={isNumeric}
        externalLabel={extField.label}
      />
    );
  }

  return (
    <div className="space-y-1.5">
      {externalValues.map(ev => (
        <div key={ev.value} className="flex items-center gap-2 py-0.5">
          <div className="flex items-center gap-1.5 w-44 flex-shrink-0">
            {ev.color && (
              <span className="h-3 w-3 flex-shrink-0 border border-[var(--rule)]" style={{ backgroundColor: ev.color }} />
            )}
            <span className="text-xs text-[var(--text-sec)] truncate">{ev.label}</span>
          </div>
          <ArrowRight className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
          {isNumeric ? (
            <input
              type="number"
              value={getVal(ev.value)}
              onChange={e => setVal(ev.value, e.target.value)}
              placeholder="Number"
              className="w-20 text-xs border border-[var(--rule)] rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500"
            />
          ) : (
            <select
              value={getVal(ev.value)}
              onChange={e => setVal(ev.value, e.target.value)}
              className="flex-1 text-xs border border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">— not mapped —</option>
              {internalOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
        </div>
      ))}
    </div>
  );
}

/** Value pairs for a field with no fixed list: each external value typed as it appears there. */
function FreeValueMapEditor({
  valueMap, onChange, internalOptions, isNumeric, externalLabel,
}: {
  valueMap: Array<{ externalValue: string; internalValue: string }>;
  onChange: (vm: Array<{ externalValue: string; internalValue: string }>) => void;
  internalOptions: Array<{ value: string; label: string }>;
  isNumeric: boolean;
  externalLabel: string;
}) {
  const [draft, setDraft] = useState('');
  const setAt = (i: number, patch: Partial<{ externalValue: string; internalValue: string }>) =>
    onChange(valueMap.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const add = () => {
    const ext = draft.trim();
    if (!ext || valueMap.some(v => v.externalValue === ext)) return;
    onChange([...valueMap, { externalValue: ext, internalValue: '' }]);
    setDraft('');
  };
  return (
    <div className="space-y-1.5">
      {valueMap.map((v, i) => (
        <div key={`${v.externalValue}-${i}`} className="flex items-center gap-2 py-0.5">
          <input
            value={v.externalValue}
            onChange={e => setAt(i, { externalValue: e.target.value })}
            aria-label={`${externalLabel} value`}
            className="w-44 flex-shrink-0 text-xs border border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)]"
          />
          <ArrowRight className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
          {isNumeric || internalOptions.length === 0 ? (
            <input
              type={isNumeric ? 'number' : 'text'}
              value={v.internalValue}
              onChange={e => setAt(i, { internalValue: e.target.value })}
              placeholder={isNumeric ? 'Number' : 'Value'}
              aria-label={`Value for ${v.externalValue}`}
              className="w-24 text-xs border border-[var(--rule)] rounded px-2 py-1"
            />
          ) : (
            <select
              value={v.internalValue}
              onChange={e => setAt(i, { internalValue: e.target.value })}
              aria-label={`Value for ${v.externalValue}`}
              className="flex-1 text-xs border border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)]"
            >
              <option value="">— not mapped —</option>
              {internalOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
          <button type="button" onClick={() => onChange(valueMap.filter((_, j) => j !== i))}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)]" aria-label={`Remove ${v.externalValue}`}>
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={`A ${externalLabel.toLowerCase()} value, as it appears there`}
          aria-label={`New ${externalLabel.toLowerCase()} value`}
          className="w-64 text-xs border border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)]"
        />
        <button type="button" onClick={add} className="inline-flex items-center gap-1 text-xs text-[var(--text-sec)] hover:underline">
          <Plus className="h-3 w-3" /> Add value
        </button>
      </div>
    </div>
  );
}

/** The connector's display name: "Linear", "Jira". */
function systemNameOf(connectorType: string): string {
  return getAllConnectors().find(c => c.type === connectorType)?.label ?? connectorType;
}

/**
 * Who keeps the field, from the rule's direction (canon 01 › Field Source, Sync Direction and
 * Conflict Rule): into Priorities.ai → the other system; out of it → Priorities.ai; both ways → shared.
 */
function sourceSentence(direction: MappingDirection, system: string): string {
  if (direction === 'import') return `Source: ${system}. Its value stands; the field is not edited in Priorities.ai.`;
  if (direction === 'export') return `Source: Priorities.ai. Its value is copied to ${system}.`;
  return `Shared with ${system}: either side may change it.`;
}

/** A direction the connector cannot carry for this kind of target yet. */
function notCarriedYet(direction: MappingDirection, connectorType: string, internalFieldKey: string, externalFieldKey: string): string | null {
  if (!attributeKeyOfTarget(internalFieldKey) || direction === 'import') return null;
  if (connectorType !== 'linear' && connectorType !== 'jira') return null;
  if (ENGINE_WRITABLE_FIELDS[connectorType]?.has(externalFieldKey)) return null;
  const system = systemNameOf(connectorType);
  return `Sending a value to this ${system} field is not built yet — this rule only brings ${system}'s value in.`;
}

// ── Single mapping entry row ──────────────────────────────────────────────────

function MappingRow({
  entry, connectorType, onChange, onRemove,
}: {
  entry: FieldMappingEntry;
  connectorType: string;
  onChange: (u: FieldMappingEntry) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const externalFields = CONNECTOR_FIELDS[connectorType] ?? [];
  const attributes = useAttributesStore((s) => s.attributes);
  const attributesLoaded = useAttributesStore((s) => !!s.syncState.lastSyncAt);
  const attributeFields = useMemo(() => attributeTargets(attributes), [attributes]);
  const customFields = useMemo(() => customFieldTargets(attributes), [attributes]);
  const internalGroups = groupInternalFields(attributeFields, customFields);

  const extField = externalFields.find(f => f.key === entry.externalFieldKey);
  const intField = internalFieldDef(entry.internalFieldKey, attributeFields, customFields);
  const canValueMap = needsValueMap(extField, intField);
  // A saved rule may name an attribute this workspace does not have (e.g. the
  // old built-in "importance"): keep it visible and editable, and say so.
  const targetAttributeKey = attributeKeyOfTarget(entry.internalFieldKey);
  const attributeNotListed = !!targetAttributeKey && !attributeFields.some(f => f.key === entry.internalFieldKey);
  const attributeMissing = attributeNotListed && attributesLoaded;
  const update = (patch: Partial<FieldMappingEntry>) => onChange({ ...entry, ...patch });
  // An attribute measured on several scales: the rule names the one its value lands on,
  // and its value list offers that scale's points.
  const targetScales = (targetAttributeKey ? attributes.find(a => a.key === targetAttributeKey)?.scales : undefined) ?? [];
  const targetAttr = targetAttributeKey ? attributes.find(a => a.key === targetAttributeKey) : undefined;
  const chosenScale = targetScales.find(sc => sc.id === entry.scaleId)
    ?? targetScales.find(sc => sc.id === targetAttr?.defaultScaleId)
    ?? targetScales[0];
  const scaleChoices = chosenScale?.points?.length
    ? chosenScale.points.map(p => ({ value: String(p.label), label: String(p.label) }))
    : undefined;
  const carriedNote = entry.internalFieldKey ? notCarriedYet(entry.direction, connectorType, entry.internalFieldKey, entry.externalFieldKey) : null;

  return (
    <div className={`border  overflow-hidden ${entry.enabled ? 'border-[var(--rule)]' : 'border-[var(--rule)] opacity-60'}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[var(--bg-raised)]">
        <button type="button" onClick={() => update({ enabled: !entry.enabled })}
          className="flex-shrink-0 text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors">
          {entry.enabled
            ? <ToggleRight className="h-5 w-5 text-[var(--text-ter)]" />
            : <ToggleLeft  className="h-5 w-5" />}
        </button>

        {/* External field */}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-0.5">External</div>
          <select
            value={entry.externalFieldKey}
            onChange={e => update({ externalFieldKey: e.target.value, valueMap: [] })}
            className="w-full text-xs border border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">— choose field —</option>
            {externalFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>

        {/* Direction */}
        <div className="flex-shrink-0 w-40">
          <div className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-0.5">Direction</div>
          <select
            value={entry.direction}
            onChange={e => update({ direction: e.target.value as MappingDirection })}
            className="w-full text-xs border border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] focus:ring-1 focus:ring-indigo-500"
          >
            <option value="import">Into Priorities.ai</option>
            <option value="export">Out of Priorities.ai</option>
            <option value="both">Both ways</option>
          </select>
        </div>

        {/* Internal field */}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-0.5">Priorities.ai Field</div>
          <select
            value={entry.internalFieldKey}
            onChange={e => update({ internalFieldKey: e.target.value, valueMap: [] })}
            className="w-full text-xs border border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">— choose field —</option>
            {Object.entries(internalGroups).map(([cat, fields]) => {
              const showSaved = cat === 'attribute' && attributeNotListed;
              if (fields.length === 0 && !showSaved) return null;
              return (
                <optgroup key={cat} label={INTERNAL_FIELD_CATEGORIES[cat as keyof typeof INTERNAL_FIELD_CATEGORIES] ?? cat}>
                  {fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                  {showSaved && (
                    <option value={entry.internalFieldKey}>
                      {humanizeAttributeKey(targetAttributeKey ?? '')}{attributeMissing ? ' (not in this workspace)' : ''}
                    </option>
                  )}
                </optgroup>
              );
            })}
          </select>
        </div>

        {targetScales.length > 1 && (
          <div className="flex-shrink-0 w-36">
            <div className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-0.5">Scale</div>
            <select
              value={chosenScale?.id ?? ''}
              onChange={e => update({ scaleId: e.target.value || undefined, valueMap: [] })}
              className="w-full text-xs border border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] focus:ring-1 focus:ring-indigo-500"
            >
              {targetScales.map(sc => <option key={sc.id} value={sc.id}>{sc.label}</option>)}
            </select>
          </div>
        )}

        {/* Value map + delete */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {canValueMap && (
            <button type="button" onClick={() => setExpanded(x => !x)}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${expanded ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'}`}>
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Values
              {entry.valueMap.length > 0 && (
                <span className="bg-[var(--slate)] text-white text-[9px] font-bold rounded-full px-1 ml-0.5">{entry.valueMap.length}</span>
              )}
            </button>
          )}
          <button type="button" onClick={onRemove}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {entry.externalFieldKey && entry.internalFieldKey && (
        <div className="px-3 py-1 bg-[var(--surface)] border-t border-[var(--rule)] text-xs text-[var(--text-sec)]" data-testid="mapping-row-source">
          {sourceSentence(entry.direction, systemNameOf(connectorType))}
          {carriedNote && <span className="ml-1 text-[var(--mustard-text,var(--text-sec))]">{carriedNote}</span>}
        </div>
      )}

      {attributeMissing && (
        <div className="px-3 py-1.5 bg-[var(--surface)] border-t border-[var(--rule)] text-xs text-[var(--coral-text)]">
          This workspace has no attribute called “{targetAttributeKey}”. Pick one above, or{' '}
          <Link to={ATTRIBUTES_SETTINGS_PATH} className="font-medium underline hover:no-underline">
            add it in Settings → Attributes
          </Link>
          .
        </div>
      )}

      {(extField?.description || intField?.description) && (
        <div className="flex gap-4 px-3 py-1 bg-[var(--surface)] border-t border-[var(--rule)] text-xs text-[var(--text-ter)]">
          {extField?.description && <span className="flex-1"><span className="font-medium text-[var(--text-sec)]">External:</span> {extField.description}</span>}
          {intField?.description && <span className="flex-1"><span className="font-medium text-[var(--text-sec)]">Internal:</span> {intField.description}</span>}
        </div>
      )}

      {expanded && canValueMap && extField && intField && (
        <div className="px-4 py-3 bg-[var(--surface)] border-t border-[var(--rule)]">
          <p className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">
            {extField.label} → {intField.label}
          </p>
          <ValueMapEditor extField={extField} intField={intField} valueMap={entry.valueMap}
            internalChoices={scaleChoices}
            onChange={vm => update({ valueMap: vm })} />
        </div>
      )}
    </div>
  );
}

// ── Mapping set editor (expanded inline) ─────────────────────────────────────

function SetEditor({
  set, connectorType, onSave, onCancel,
}: {
  set: FieldMappingSet;
  connectorType: string;
  onSave: (updated: FieldMappingSet) => void;
  onCancel: () => void;
}) {
  const [entries, setEntries] = useState<FieldMappingEntry[]>(set.entries);

  const addEntry = () => {
    setEntries(prev => [...prev, {
      id: uid(), externalFieldKey: '', internalFieldKey: '',
      direction: 'import', enabled: true, valueMap: [],
    }]);
  };

  const updateEntry = (id: string, updated: FieldMappingEntry) =>
    setEntries(prev => prev.map(e => e.id === id ? updated : e));

  const removeEntry = (id: string) =>
    setEntries(prev => prev.filter(e => e.id !== id));

  const handleSave = () => onSave({ ...set, entries, updatedAt: now() });

  const connectorFields = CONNECTOR_FIELDS[connectorType];

  return (
    <div className="border-t border-[var(--rule)] bg-[var(--surface)]/50 px-4 py-4 space-y-3">
      {!connectorFields && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-xs text-[var(--mustard)]">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          No field schema registered for this connector yet.
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-sm text-[var(--text-ter)] text-center py-4">No field rules yet. Add one below.</p>
      ) : (
        <div className="space-y-2">
          {entries.map(e => (
            <MappingRow key={e.id} entry={e} connectorType={connectorType}
              onChange={updated => updateEntry(e.id, updated)}
              onRemove={() => removeEntry(e.id)} />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <button type="button" onClick={addEntry}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] border border-[var(--slate)] transition-colors">
          <Plus className="h-3.5 w-3.5" /> Add Field Rule
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel}
            className="px-3 py-1.5 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleSave}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors">
            <Save className="h-3.5 w-3.5" /> Save Set
          </button>
        </div>
      </div>

      {/* Field reference */}
      {connectorFields && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider hover:text-[var(--text-sec)] flex items-center gap-1">
            <Info className="h-3 w-3" /> Available fields ({connectorFields.length})
          </summary>
          <div className="mt-2 grid gap-1.5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {connectorFields.map(f => (
              <div key={f.key} className="text-xs bg-[var(--bg-raised)] border border-[var(--rule)] rounded px-2.5 py-1.5">
                <div className="font-medium text-[var(--text-pri)]">{f.label}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="font-mono bg-[var(--surface)] border border-[var(--rule)] px-1 rounded text-[10px]">{f.type}</span>
                  <span className="text-[10px] text-[var(--text-ter)] capitalize">{f.direction}</span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ── Mapping set card ──────────────────────────────────────────────────────────

/** Connectors whose imports remember what each record said, so a set can be applied afterwards. */
const APPLY_LATER_CONNECTORS = new Set(['miro']);

function SetCard({
  set, connectorType, isImportActive, isExportActive,
  onSave, onDelete, onDuplicate, onRename, applyLater,
}: {
  set: FieldMappingSet;
  connectorType: string;
  isImportActive: boolean;
  isExportActive: boolean;
  onSave: (updated: FieldMappingSet) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onRename: (name: string, description: string) => void;
  /** Present only where "Apply to imported items" can do something. */
  applyLater?: { connectorLabel: string; isConnected: boolean; workspaceId: string };
}) {
  const [editing, setEditing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(set.name);
  const [draftDesc, setDraftDesc] = useState(set.description);

  const handleSaveRename = () => {
    onRename(draftName.trim() || set.name, draftDesc.trim());
    setRenaming(false);
  };

  const enabledCount = set.entries.filter(e => e.enabled).length;

  return (
    <div className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)]">
      {/* Card header */}
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          {renaming ? (
            <div className="space-y-2">
              <input
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                className="w-full text-sm font-medium border border-[var(--rule)] rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500"
                placeholder="Set name"
                autoFocus
              />
              <input
                value={draftDesc}
                onChange={e => setDraftDesc(e.target.value)}
                className="w-full text-xs border border-[var(--rule)] rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500"
                placeholder="Description (optional)"
              />
              <div className="flex gap-2">
                <button type="button" onClick={handleSaveRename}
                  className="px-2 py-1 text-xs bg-[var(--slate)] text-white rounded hover:bg-[var(--slate)] transition-colors">Save</button>
                <button type="button" onClick={() => setRenaming(false)}
                  className="px-2 py-1 text-xs text-[var(--text-sec)] border border-[var(--rule)] rounded hover:bg-[var(--surface)] transition-colors">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-[var(--text-pri)]">{set.name}</span>
                {isImportActive && (
                  <span className="flex items-center gap-1 text-[10px] font-semibold bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full px-2 py-0.5">
                    <ArrowDown className="h-2.5 w-2.5" /> Import
                  </span>
                )}
                {isExportActive && (
                  <span className="flex items-center gap-1 text-[10px] font-semibold bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] rounded-full px-2 py-0.5">
                    <ArrowUp className="h-2.5 w-2.5" /> Export
                  </span>
                )}
              </div>
              {set.description && <p className="text-xs text-[var(--text-ter)] mt-0.5">{set.description}</p>}
              <p className="text-xs text-[var(--text-ter)] mt-0.5">
                {set.entries.length} rule{set.entries.length !== 1 ? 's' : ''}
                {set.entries.length > 0 && enabledCount < set.entries.length
                  ? ` · ${enabledCount} enabled`
                  : ''}
                {' · '}updated {new Date(set.updatedAt).toLocaleDateString()}
              </p>
            </>
          )}
        </div>

        {/* Actions */}
        {!renaming && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {applyLater && (
              <button type="button" {...btnSecondary()} onClick={() => setApplying(x => !x)} aria-expanded={applying}>
                {applying ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                Apply to imported items
              </button>
            )}
            <button type="button" onClick={() => setEditing(x => !x)}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${editing ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--hover-tint)]'}`}>
              {editing ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {editing ? 'Close' : 'Edit'}
            </button>
            <button type="button" onClick={() => { setDraftName(set.name); setDraftDesc(set.description); setRenaming(true); }}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] rounded transition-colors" title="Rename">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={onDuplicate}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] rounded transition-colors" title="Duplicate">
              <Copy className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={onDelete}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded transition-colors" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Inline editor */}
      {editing && (
        <SetEditor
          set={set}
          connectorType={connectorType}
          onSave={updated => { onSave(updated); setEditing(false); }}
          onCancel={() => setEditing(false)}
        />
      )}

      {applying && applyLater && (
        <ApplyMappingSetPanel set={set} connectorType={connectorType} {...applyLater} />
      )}
    </div>
  );
}

// ── Connector panel ───────────────────────────────────────────────────────────

function ConnectorPanel({
  connectorType, connectorLabel, isConnected, workspaceId,
}: {
  connectorType: string;
  connectorLabel: string;
  isConnected: boolean;
  workspaceId: string | null;
}) {
  const [config, setConfig] = useState<FieldMappingConfig>({ version: 2, sets: [], importSetId: null, exportSetId: null });
  const [loading, setLoading] = useState(true);
  const [savedFlash, setSavedFlash] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    loadFieldMappingConfig(workspaceId, connectorType).then(cfg => {
      setConfig(cfg);
      setLoading(false);
    });
  }, [workspaceId, connectorType]);

  const persist = useCallback(async (next: FieldMappingConfig) => {
    if (!workspaceId) { setSaveError('No workspace — cannot save.'); return; }
    setConfig(next);
    const { error } = await saveFieldMappingConfig(workspaceId, connectorType, next);
    if (error) {
      setSaveError(error);
    } else {
      setSavedFlash(true);
      setSaveError(null);
      setTimeout(() => setSavedFlash(false), 2000);
    }
  }, [workspaceId, connectorType]);

  const sets = config.sets;

  const addSet = () => {
    const n = sets.length + 1;
    const newSet: FieldMappingSet = {
      id: uid(), connectorType,
      name: n === 1 ? 'Default' : `Set ${n}`,
      description: '',
      entries: [],
      createdAt: now(),
      updatedAt: now(),
    };
    // A new set becomes the active one for any direction that has none, as
    // "Load recommended Jira set" does — so it runs without a second step.
    persist({
      ...config,
      sets: [...sets, newSet],
      importSetId: config.importSetId ?? newSet.id,
      exportSetId: config.exportSetId ?? newSet.id,
    });
  };

  const updateSet = (updated: FieldMappingSet) =>
    persist({ ...config, sets: sets.map(s => s.id === updated.id ? updated : s) });

  const deleteSet = (id: string) => {
    persist({
      ...config,
      sets: sets.filter(s => s.id !== id),
      importSetId: config.importSetId === id ? null : config.importSetId,
      exportSetId: config.exportSetId === id ? null : config.exportSetId,
    });
  };

  const duplicateSet = (set: FieldMappingSet) => {
    const copy: FieldMappingSet = { ...set, id: uid(), name: `${set.name} (copy)`, createdAt: now(), updatedAt: now() };
    persist({ ...config, sets: [...sets, copy] });
  };

  const renameSet = (id: string, name: string, description: string) =>
    persist({ ...config, sets: sets.map(s => s.id === id ? { ...s, name, description, updatedAt: now() } : s) });

  const savePrefs = (importSetId: string | null, exportSetId: string | null) =>
    persist({ ...config, importSetId, exportSetId });

  if (loading) {
    return <div className="p-6 text-sm text-[var(--text-ter)]">Loading…</div>;
  }

  return (
    <div className="p-5 space-y-5">
      {/* Deployment bar */}
      <div className="flex items-start gap-4 p-4 bg-[var(--surface)] border border-[var(--rule)]">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-[var(--text-sec)] mb-1 flex items-center gap-1.5">
            <ArrowDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            Active set on <span className="text-[var(--text-ter)]">Import</span>
          </div>
          <select
            value={config.importSetId ?? ''}
            onChange={e => savePrefs(e.target.value || null, config.exportSetId)}
            className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">— none —</option>
            {sets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-[var(--text-sec)] mb-1 flex items-center gap-1.5">
            <ArrowUp className="h-3.5 w-3.5 text-[var(--green)]" />
            Active set on <span className="text-[var(--green)] dark:text-[var(--text-ter)]">Export</span>
          </div>
          <select
            value={config.exportSetId ?? ''}
            onChange={e => savePrefs(config.importSetId, e.target.value || null)}
            className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">— none —</option>
            {sets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex flex-col items-end justify-center pt-5 flex-shrink-0 gap-1">
          {savedFlash && (
            <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]"><Check className="h-3.5 w-3.5" /> Saved</span>
          )}
          {saveError && (
            <span className="flex items-center gap-1 text-xs text-[var(--coral)]"><AlertCircle className="h-3.5 w-3.5" /> {saveError}</span>
          )}
          {!workspaceId && (
            <span className="text-xs text-[var(--mustard)]">No active workspace</span>
          )}
        </div>
      </div>

      {/* Connection notice if not connected (skip for capture-only sources) */}
      {!isConnected && connectorType !== 'whiteboard' && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
          <WifiOff className="h-4 w-4 flex-shrink-0" />
          <span>
            {connectorLabel} is not connected. You can configure mappings in advance —{' '}
            <Link to="/prioritization/settings/connectors" className="underline hover:no-underline">connect it here</Link>{' '}
            to deploy them.
          </span>
        </div>
      )}

      {connectorType === 'whiteboard' && (
        <div className="flex items-start gap-2 px-4 py-2.5 bg-[var(--slate-dim)] border border-[var(--slate)] text-sm text-[var(--text-sec)]">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            Whiteboard photo import reads visual channels from the board photo — no OAuth connection.
            Use the Visual language section below for the six primary channels; named mapping sets
            hold flat field→field value maps when you need them.
          </span>
        </div>
      )}

      {/* Set list */}
      {sets.length === 0 ? (
        <div className="text-center py-8 text-[var(--text-ter)]">
          <p className="text-sm">No mapping sets yet for {connectorLabel}.</p>
          <p className="text-xs mt-1 text-[var(--text-ter)]">Create a set to define how fields translate during import and export.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sets.map(s => (
            <SetCard
              key={s.id}
              set={s}
              connectorType={connectorType}
              isImportActive={config.importSetId === s.id}
              isExportActive={config.exportSetId === s.id}
              onSave={updateSet}
              onDelete={() => deleteSet(s.id)}
              onDuplicate={() => duplicateSet(s)}
              onRename={(name, desc) => renameSet(s.id, name, desc)}
              applyLater={workspaceId && APPLY_LATER_CONNECTORS.has(connectorType)
                ? { connectorLabel, isConnected, workspaceId }
                : undefined}
            />
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button type="button" onClick={addSet}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] border border-[var(--slate)] transition-colors">
          <Plus className="h-4 w-4" />
          New Mapping Set
        </button>
        {connectorType === 'jira' && !sets.some(s => s.id === 'jira-default') && (
          <button
            type="button"
            onClick={() => {
              // The profile-shipped intelligent defaults, finally applied
              // (semantic-connectors 01-AUDIT defect #7). Becomes the active
              // set for any direction that has none.
              const recommended = getDefaultJiraMappingSet();
              persist({
                ...config,
                sets: [...sets, recommended],
                importSetId: config.importSetId ?? recommended.id,
                exportSetId: config.exportSetId ?? recommended.id,
              });
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] hover:bg-[var(--green-dim)] border border-[var(--green)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Load recommended Jira set
          </button>
        )}
      </div>

      {/* Whiteboard visual language — six primary channels */}
      {connectorType === 'whiteboard' && (
        <WhiteboardVisualLanguageSection workspaceId={workspaceId} />
      )}

      {/* Semantic map v3 — only for connectors with a semantic profile */}
      {getConnectorProfile(connectorType) && (
        <SemanticRulesSection
          connectorType={connectorType}
          rules={config.relationshipMap ?? []}
          onChange={relationshipMap => persist({ ...config, version: 3, relationshipMap })}
        />
      )}
    </div>
  );
}

// ── Semantic rules (v3) ───────────────────────────────────────────────────────
// The Settings field maps evolved into semantic maps (02-DESIGN §2.3): for
// connectors with a semantic profile, dialect cues (captions, line styles,
// containment) resolve to canonical relationship types here. Workspace rules
// override the profile-shipped defaults; no second mapping UI.

const CUE_KINDS: RelationshipCueKind[] = ['caption', 'symbol', 'link_type', 'edge_kind', 'containment'];
const CANONICAL_TYPES = ['related', 'dependency', 'prerequisite', 'aggregation', 'elaboration', 'breakdown', 'contains'];
const RULE_BASES: RelationshipCueRule['basis'][] = ['explicit', 'symbol', 'visual_language', 'spatial'];

function SemanticRulesSection({
  connectorType,
  rules,
  onChange,
}: {
  connectorType: string;
  rules: RelationshipCueRule[];
  onChange: (rules: RelationshipCueRule[]) => void;
}) {
  const defaults = getDefaultRelationshipMap(connectorType);
  const [draft, setDraft] = useState<RelationshipCueRule>({
    cue: { kind: 'caption', value: '' },
    canonicalType: 'dependency',
    directionality: 'directed',
    defaultConfidence: 1,
    basis: 'explicit',
  });

  return (
    <div className="border border-[var(--rule)] p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-[var(--text-pri)]">Relationship rules (semantic map v3)</div>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          How this connector's cues become canonical relationships on excursion return. Workspace rules
          run before the {defaults.length} profile defaults. Explicit cues write edges directly; spatial
          cues become review proposals.
        </p>
      </div>

      {rules.length > 0 && (
        <ul className="space-y-1">
          {rules.map((rule, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
              <span className="font-mono text-xs bg-[var(--hover-tint)] px-1.5 py-0.5 rounded">{rule.cue.kind}</span>
              <span className="font-mono text-xs">“{rule.cue.value}”</span>
              <span className="text-[var(--text-ter)]">→</span>
              <span>{rule.canonicalType}{rule.subtype ? ` (${rule.subtype})` : ''}</span>
              <span className="font-mono text-xs text-[var(--text-ter)]">{rule.basis} · {Math.round(rule.defaultConfidence * 100)}%</span>
              <button
                type="button"
                onClick={() => onChange(rules.filter((_, j) => j !== i))}
                className="ml-auto text-[var(--text-ter)] hover:text-[var(--coral)]"
                aria-label="Delete rule"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-[var(--text-ter)]">
          Cue
          <select
            value={draft.cue.kind}
            onChange={e => setDraft({ ...draft, cue: { ...draft.cue, kind: e.target.value as RelationshipCueKind } })}
            className="block mt-0.5 text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)]"
          >
            {CUE_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label className="text-xs text-[var(--text-ter)]">
          Value
          <input
            value={draft.cue.value}
            onChange={e => setDraft({ ...draft, cue: { ...draft.cue, value: e.target.value } })}
            placeholder="e.g. blocks"
            className="block mt-0.5 text-sm border border-[var(--rule)] px-2 py-1.5 w-32"
          />
        </label>
        <label className="text-xs text-[var(--text-ter)]">
          Canonical type
          <select
            value={draft.canonicalType}
            onChange={e => setDraft({ ...draft, canonicalType: e.target.value })}
            className="block mt-0.5 text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)]"
          >
            {CANONICAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="text-xs text-[var(--text-ter)]">
          Basis
          <select
            value={draft.basis}
            onChange={e => {
              const basis = e.target.value as RelationshipCueRule['basis'];
              setDraft({
                ...draft,
                basis,
                defaultConfidence: basis === 'explicit' ? 1 : Math.min(draft.defaultConfidence, 0.9),
              });
            }}
            className="block mt-0.5 text-sm border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)]"
          >
            {RULE_BASES.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label className="text-xs text-[var(--text-ter)]">
          Confidence
          <input
            type="number" min={0.1} max={1} step={0.1}
            value={draft.defaultConfidence}
            disabled={draft.basis === 'explicit'}
            onChange={e => setDraft({ ...draft, defaultConfidence: Math.max(0.1, Math.min(1, Number(e.target.value))) })}
            className="block mt-0.5 text-sm border border-[var(--rule)] px-2 py-1.5 w-20 disabled:bg-[var(--surface)]"
          />
        </label>
        <button
          type="button"
          disabled={!draft.cue.value.trim()}
          onClick={() => {
            onChange([...rules, { ...draft, cue: { ...draft.cue, value: draft.cue.value.trim() } }]);
            setDraft({ ...draft, cue: { ...draft.cue, value: '' } });
          }}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] disabled:opacity-50 border border-[var(--slate)]"
        >
          <Plus className="h-3.5 w-3.5" /> Add rule
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// CSV is not a Field Mappings tab — Citizenship owns profiles + schedule.
const FIELD_MAPPING_CONNECTORS = getAllConnectors().filter((c) => c.type !== 'csv');

export default function FieldMappings() {
  const allConnectors = FIELD_MAPPING_CONNECTORS;
  const [activeTab, setActiveTab] = useState(allConnectors[0]?.type ?? '');
  // Once someone clicks a tab, loading never moves them off it.
  const tabChosen = useRef(false);
  const [connectedTypes, setConnectedTypes] = useState<Set<string>>(new Set());
  const workspaceId = getCurrentWorkspaceId();
  // Unified attributes ride the Semantic Connectors module (no second mapping UI).
  const semanticConnectorsOn = useSemanticConnectorsEnabled();

  // Rule targets list the workspace's own attributes.
  useEffect(() => {
    if (!useAttributesStore.getState().syncState.lastSyncAt) {
      void useAttributesStore.getState().fetchFromSupabase();
    }
  }, []);

  // Load connected connector types from DB for badge display, and open on the
  // first connector that has saved sets — else the first connected one.
  useEffect(() => {
    if (!workspaceId) return;
    fetchConnectorConfigsFromDB(workspaceId).then(({ data }) => {
      const rows = data ?? [];
      const connected = new Set(rows.filter(r => r.isActive).map(r => r.connectorType as string));
      setConnectedTypes(connected);
      if (tabChosen.current) return;
      const withSets = new Set(
        rows.filter(r => parseFieldMappingConfig(r.fieldMappings).sets.length > 0).map(r => r.connectorType as string),
      );
      const opening = FIELD_MAPPING_CONNECTORS.find(c => withSets.has(c.type))
        ?? FIELD_MAPPING_CONNECTORS.find(c => connected.has(c.type));
      if (opening) setActiveTab(opening.type);
    });
  }, [workspaceId]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ArrowLeftRight className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Field Mappings"
        subtitle="Create named mapping sets for each connector. Assign a set for import, a set for export — or the same one for both. Multiple sets per connector let you swap contexts (e.g. sprint planning vs. discovery) without losing configuration. For CSV files, use Settings → Spreadsheet sync."
      />

      {/* Context strip — whole card is the hit target (not just the title text) */}
      <div className="grid grid-cols-3 gap-3 text-sm">
        {[
          { label: 'Connectors',     desc: 'Credentials & connect / disconnect', href: '/prioritization/settings/connectors',    active: false },
          { label: 'Field Mappings', desc: 'Named mapping sets — this page',     href: '/prioritization/settings/field-mappings', active: true },
          { label: 'Entity Linker',  desc: 'Record identity (item X = issue Y)', href: '/prioritization/settings/entity-linker', active: false },
        ].map(({ label, desc, href, active }) => {
          const className = `block  border px-4 py-3 transition-colors ${
            active
              ? 'border-[var(--slate)] bg-[var(--slate-dim)] dark:border-[var(--slate)] dark:bg-[var(--slate)]/30'
              : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/50 dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:hover:border-[var(--slate)] dark:hover:bg-[var(--slate)]/20'
          }`;
          const body = (
            <>
              <div
                className={`font-semibold text-sm ${
                  active
                    ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                    : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'
                }`}
              >
                {label}
              </div>
              <p
                className={`text-xs mt-0.5 ${
                  active
                    ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                    : 'text-[var(--text-ter)] dark:text-[var(--text-sec)]'
                }`}
              >
                {desc}
              </p>
            </>
          );
          return active ? (
            <div key={label} className={className} aria-current="page">
              {body}
            </div>
          ) : (
            <Link key={label} to={href} className={className}>
              {body}
            </Link>
          );
        })}
      </div>

      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        {/* Connector tabs — one per registered schema; grows automatically */}
        <div className="flex border-b border-[var(--rule)] overflow-x-auto">
          {allConnectors.map(({ type, label }) => {
            const isCapture = type === 'whiteboard';
            const isConnected = isCapture || connectedTypes.has(type);
            return (
              <button
                key={type}
                onClick={() => { tabChosen.current = true; setActiveTab(type); }}
                className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${
                  activeTab === type
                    ? 'border-[var(--slate)] text-[var(--text-ter)]'
                    : 'border-transparent text-[var(--text-sec)] hover:text-[var(--text-pri)]'
                }`}
              >
                {label}
                {isConnected
                  ? <Wifi    className="h-3 w-3 text-[var(--green)]" title={isCapture ? 'Photo capture (always available)' : 'Connected'} />
                  : <WifiOff className="h-3 w-3 text-[var(--text-ter)]"    title="Not connected" />
                }
              </button>
            );
          })}
        </div>

        {/* Active connector panel */}
        {allConnectors.map(({ type, label }) =>
          activeTab === type ? (
            <ConnectorPanel
              key={type}
              connectorType={type}
              connectorLabel={label}
              isConnected={type === 'whiteboard' || connectedTypes.has(type)}
              workspaceId={workspaceId}
            />
          ) : null
        )}
      </div>

      {/* Cross-app attributes sit below the connector sets they build on. */}
      {semanticConnectorsOn && <UnifiedAttributesSection workspaceId={workspaceId} />}

      <div className="flex items-start gap-3 px-4 py-3 bg-[var(--surface)] border border-[var(--rule)] text-sm text-[var(--text-sec)]">
        <AlertCircle className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
        <span>
          A set applies when you import. For Miro, “Apply to imported items” on a set applies it to items already
          here; values a person set are left alone. Disable individual rules without deleting them. The import set and export set can be different —
          useful when the same connector field means something different coming in vs. going out.
        </span>
      </div>
    </div>
  );
}
